import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

/**
 * Badges, per handoff-academic/CLAUDE.md §4.
 *
 * The rule that drives every variant here: **a badge always carries a border
 * plus a tint — colour alone is never the signal.** Two reasons it matters in
 * this palette specifically. The `-subtle` fills are close in value to
 * parchment, so on their own they can read as an accidental highlight rather
 * than a deliberate state; and the light-mode warning and accent are both
 * gilt-brown, so the border is doing real work separating them.
 *
 * `default` used to be the one variant with no border, which made "Draft" the
 * only status that looked like a typo instead of a state.
 *
 * Status mapping (§4): Draft → default/muted · Under review → primary ·
 * Accepted → success · Revisions due → warning · Rejected/Blocked → error ·
 * Keynote → accent · Chair → `chair` (the one solid fill).
 */
const badgeVariants = cva(
  'inline-flex items-center font-medium rounded-md whitespace-nowrap border',
  {
    variants: {
      variant: {
        default: 'bg-muted text-muted-foreground border-border',
        primary: 'bg-primary-subtle text-primary border-primary/30',
        secondary: 'bg-muted text-foreground border-border',
        success: 'bg-success-subtle text-success border-success-border',
        warning: 'bg-warning-subtle text-warning border-warning-border',
        error: 'bg-error-subtle text-error border-error-border',
        info: 'bg-primary-subtle text-primary border-primary/30',
        accent: 'bg-accent-subtle text-accent border-accent/30',
        /* Chair is the one badge that gets a solid fill — it marks a person's
           standing rather than a record's state, so it should sit apart. */
        chair: 'bg-primary-solid text-primary-solid-foreground border-primary-solid-border',
        outline: 'bg-transparent border-border text-foreground',
      },
      size: {
        // §4: 12px / 500 / 4px 9px. `md` is that; sm and lg flank it.
        sm: 'text-[11px] px-1.5 py-0.5',
        md: 'text-xs px-[9px] py-[4px]',
        lg: 'text-xs px-2.5 py-1',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

const Badge = React.forwardRef(({
  className,
  variant,
  size,
  children,
  ...props
}, ref) => (
  <span
    ref={ref}
    className={cn(badgeVariants({ variant, size }), className)}
 {...props}
  >
    {children}
  </span>
));
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
export default Badge;
